/**
 * `Avatar` — the admin dashboard's skin over the shared `@auctogon/ui` Avatar
 * (BL-1634 AC 7 built it admin-only; BL-1701 promoted the structure and left
 * this file as the binding — ADR 030 — Design Canon § 3c core/skin split).
 *
 * Before BL-1634, six surfaces each drew their own customer circle with THREE
 * different type treatments at the same 32px box and two private `initials()`
 * helpers that disagreed with `bidderInitials`. Before BL-1701, the STAFF
 * circles were a second family again: a `.ui-avatar` CSS class in settings-v2
 * with its own 22/28/32/40 scale in the mono font, a hand-rolled 24px chip in
 * the clerk console top bar with a third initials algorithm, and an 80px
 * hand-rolled circle in the profile modal. This skin owns one treatment per
 * size and one measured token pairing per tone for all of them; the caller
 * says which SIZE and which SURFACE (tone), never which pixel. That
 * normalisation is what the Design-approval gate on each promotion granted —
 * a human grant, recorded in the PR body (ADR 030).
 *
 * `initials` is a display string on purpose: the initials FUNCTION is the
 * caller's single source — `bidderInitials` for a customer or business name,
 * `staffInitials` for a staff member (ADR 058 — Customer Identity). The icon
 * tiles (invite, super-admin) pass `''` and a lucide child instead.
 *
 * Contrast, measured with `scripts/contrast.mjs` on the surfaces each tone
 * lands on (ADR 051 — Contrast Floor; light only — admin has no dark theme):
 *   neutral          --ink-600 on --surface-paper-2   6.88:1
 *   pending          --warning-700 on --warning-50    5.46:1  (hue is not the
 *                    only signal — the circle is decorative; rule 3)
 *   outline          --ink-700 on white              10.31:1
 *   pending-outline  --warning-700 on white           5.78:1  (the
 *                    --warning-200 hairline is 1.24:1 and exempt under rule 4
 *                    — delete the edge and the avatar is still identifiable)
 *   brand            white on --teal-800              9.15:1
 *   brand-soft       --teal-800 on --teal-100         7.65:1
 *   inverse          white on 15% white over --primary (#2e6b68)   6.14:1
 *                    (the clerk console top bar's dark chrome)
 *   accent           --secondary-foreground on --secondary          4.92:1
 *                    (the profile modal — the BL-1170 token pair, unchanged)
 */

import type { CSSProperties, ReactNode } from 'react';
import { Avatar as UiAvatar, type AvatarDialect, type AvatarShape } from '@auctogon/ui/web';

/**
 * Box size — one type treatment each.
 * `xs` 24px · `sm` 28px · `md` 32px · `lg` 40px · `xl` 80px.
 */
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** Which surface the circle sits on. Each tone is a measured fill / text / edge triple. */
export type AvatarTone =
    | 'neutral'
    | 'pending'
    | 'outline'
    | 'pending-outline'
    | 'brand'
    | 'brand-soft'
    | 'inverse'
    | 'accent';

export type { AvatarShape };

/**
 * Admin's dialect. Type sizes are steps on the BL-1841 scale — 10 / 10 / 12 /
 * 16 / 24, never an in-between value. They were 11.5 / 11.5 / 12.5 / 15 / 24 on
 * the retired `--text-*` scale.
 *
 * `micro` (10px) exists BECAUSE of this component, and this is the whole of its
 * sanctioned use: text sized to the container it sits in rather than to a
 * reading line. Without it `xs`, `sm` and `md` all land on `caption` and the
 * three smallest circles render identical letters at 24, 28 and 32px — a ramp of
 * circles with no ramp of initials. Read the step's own docblock in
 * `packages/ui/src/isomorphic/tokens/typography.ts` before reaching for it
 * anywhere else; standalone 10px text does not clear legibility.
 *
 * **`sm` does NOT take `micro`, and the reason is a signed constraint rather
 * than taste.** BL-1403 set the clerk console a legibility floor of 11.5px, and
 * `CustomerIdentity` renders `size="sm"` on the console's rail and bid record —
 * so 10px there would breach it. `sm` keeps `caption`, which clears the floor at
 * 12px. That leaves `sm` and `md` sharing a step, which is a real loss and the
 * smaller of the two available ones: without `micro` anywhere, `xs`, `sm` AND
 * `md` all collapse onto 12.
 *
 * Moving `sm` to `micro` means re-signing that floor first.
 */
const ADMIN_AVATAR_DIALECT: AvatarDialect<AvatarSize, AvatarTone> = {
    // `font-medium`, not `font-semibold` (2026-09-22): initials are a label on a
    // coloured disc, not emphasis. At 10–12px semibold closes the counters and
    // the two letters read as one shape — the weight was doing the work the
    // tone's contrast already does.
    root: 'inline-flex flex-none select-none items-center justify-center overflow-hidden font-medium',
    sizes: {
        // BLOCKED on a re-sign, not a preference: this wants `text-micro`, and
        // `xs`'s ONE production consumer is `ClerkConsoleTopBar` — a screen whose
        // legibility floor (BL-1403 AC 5, signed) is "no text below 11.5px".
        // Flip this line the day that floor is amended; the step is bound and
        // waiting.
        xs: 'h-6 w-6 text-caption',
        sm: 'h-7 w-7 text-caption',
        md: 'h-8 w-8 text-caption',
        lg: 'h-10 w-10 text-body-lg',
        xl: 'h-20 w-20 text-title-lg',
    },
    tones: {
        neutral: 'bg-[var(--surface-paper-2)] text-[var(--ink-600)]',
        pending: 'bg-[var(--warning-50)] text-[var(--warning-700)]',
        outline: 'bg-white border text-[var(--ink-700)]',
        // The hairline is `--warning-200` — the same edge the pending band and its
        // reject button draw (BL-1635 tokenised the hex both used to spell).
        'pending-outline': 'bg-white border border-[var(--warning-200)] text-[var(--warning-700)]',
        brand: 'bg-[var(--teal-800)] text-white',
        'brand-soft': 'bg-[var(--teal-100)] text-[var(--teal-800)]',
        inverse: 'bg-white/15 text-white',
        accent: 'bg-secondary text-secondary-foreground',
    },
    circle: 'rounded-full',
    // `.ui-avatar--square`'s corner — the two settings rails keep it.
    square: 'rounded-[var(--radius-md)]',
    image: 'h-full w-full object-cover',
};

export interface AvatarProps {
    /** The initials to draw, already derived by the caller's single initials source. */
    initials: string;
    /** A photo URL — replaces the initials and any icon child. */
    src?: string | null;
    /** An icon to draw instead of the initials. */
    children?: ReactNode;
    /** Defaults to `md`. */
    size?: AvatarSize;
    /** Defaults to `neutral`. */
    tone?: AvatarTone;
    /** Defaults to `circle`. */
    shape?: AvatarShape;
    /** Extra classes on the circle — the caller's spacing lives here. */
    className?: string;
    /** When set, the circle is an image with this accessible name instead of decorative. */
    label?: string;
    /** Native tooltip on the box. */
    title?: string;
    /** Inline style on the box. */
    style?: CSSProperties;
    /** Automation handle on the box (BL-2622). */
    testId?: string;
}

/**
 * The admin avatar. See the file header for the size/tone contract.
 *
 * @param props - initials + size + tone; `label` opts into an accessible image.
 * @returns the shared core bound to admin's dialect.
 */
export default function Avatar({ size = 'md', tone = 'neutral', ...rest }: AvatarProps) {
    return <UiAvatar dialect={ADMIN_AVATAR_DIALECT} size={size} tone={tone} {...rest} />;
}
